import {
  Button,
  Heading,
  Illustration,
  Shadow,
  SliderModal,
  Text,
  useAlert,
  MenuSelect,
  Icon,
  NewAccordion,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, Center, Pressable, Spacer } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRateManagementActions } from '../../../store/rateManagement/actions';
import { rateManagementUpload } from '../../../store/rateManagement/selector';

import { FileField } from './components/FileField';

export namespace RateManagementUploadTypes {
  export type Props = {
    isOpen?: boolean;
  };
}

export const RateManagementUpload: React.FC<RateManagementUploadTypes.Props> = () => {
  const navigation = useNavigation();
  const [file, setFile] = React.useState<File | undefined>(undefined);
  const [fileError, setFileError] = React.useState<boolean>(false);
  const { alert } = useAlert();
  const { isUploadFailed, isUploaded, isUploading } = useRecoilValue(rateManagementUpload);
  const { uploadSpreadsheet, resetUploadState, notifyOrderUploaded, downloadSpreadsheet } =
    useRateManagementActions();

  const [competence, setCompetence] = React.useState<{ value: string; label: string }>({
    value: ' ',
    label: ' ',
  });

  const width = useBreakpointValue({
    base: 1,
    md: 0.5,
  });

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const handleDismiss = useCallback(() => {
    if (isUploading) {
      return;
    }

    if (isUploaded) {
      alert(
        {
          icon: 'BackInTime',
          description:
            'Esta solicitação pode demorar alguns minutos, aguarde a conclusão do processo, que será indicada pelo status DISPONÍVEL.',
        },
        () => {
          goBack();
        }
      );
    }

    goBack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goBack, isUploaded, isUploading]);

  const handleDownload = useCallback(() => {
    downloadSpreadsheet();
  }, [downloadSpreadsheet]);

  const months = useMemo(() => {
    const today = new Date();
    const monthList: { label: string; value: string }[] = [];

    for (let i = 1; i < 4; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      monthList.push({
        label: `${date.toLocaleString('pt-BR', {
          month: 'long',
        })}/${date.getFullYear()}`.toUpperCase(),
        value: date.getFullYear() + (date.getMonth() + 1).toString().padStart(2, '0'),
      });
    }

    setCompetence({ value: monthList[0].value, label: monthList[0].label });

    return monthList;
  }, []);

  const uploadSection = useMemo(() => {
    const handleReset = () => {
      setFile(undefined);
      resetUploadState();
    };

    const handleError = () => {
      setFileError(true);
    };

    const handleFileChange = (selectedFile: File) => {
      setFileError(false);
      setFile(selectedFile);
    };

    const handleImport = () => {
      if (!file) {
        return;
      }

      uploadSpreadsheet(file, competence.value);
    };

    if (isUploading) {
      return (
        <Shadow
          p={Spacing['inline.xxxs']}
          testId="uploading-shadow"
          level={2}
          borderWidth={1}
          borderColor="transparent"
          borderRadius={BoxRadius.sm}>
          <Center>
            <Icon
              name="Info"
              size="md"
              testId="download-icon"
              color={Color['brand.tertiary.principal']}
            />
            <Text>Aguarde, seu arquivo está sendo enviado</Text>
          </Center>
        </Shadow>
      );
    }

    if (isUploadFailed) {
      return (
        <Shadow
          p={Spacing['inline.xxxs']}
          testId="uploading-shadow"
          level={2}
          borderWidth={1}
          borderColor={Color['feedback.error.default']}
          borderRadius={BoxRadius.sm}>
          <HStack space={Spacing['stack.quarck']} alignItems="center">
            <Icon
              name="Warning"
              size="sm"
              testId="download-icon"
              color={Color['feedback.error.default']}
            />
            <VStack space={Spacing['stack.quarck']}>
              <Text variant="bold">{file?.name.toUpperCase()}</Text>
              <Text fontSize={Typography['fontsize.xs']}>Erro ao enviar o arquivo</Text>
            </VStack>
            <Spacer />
            <Pressable onPress={handleReset}>
              <Icon
                name="CloseModal"
                size="md"
                testId="error-icon"
                color={Color['feedback.error.default']}
              />
            </Pressable>
          </HStack>
        </Shadow>
      );
    }

    return (
      <>
        <FileField onReset={handleReset} onError={handleError} onSelectFile={handleFileChange} />
        <Center>
          <Button testID="import-button" disabled={fileError || !file} onPress={handleImport}>
            Importar
          </Button>
        </Center>
      </>
    );
  }, [
    competence,
    file,
    fileError,
    isUploadFailed,
    isUploading,
    resetUploadState,
    uploadSpreadsheet,
  ]);

  useEffect(() => {
    return () => {
      resetUploadState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUploaded) {
      handleDismiss();
      notifyOrderUploaded();
    }
  }, [isUploaded, handleDismiss, notifyOrderUploaded]);

  const deviceType = useBreakpointValue({
    base: 'mobile',
    sm: 'desktop',
  });

  const [accordionState, setAccordionState] = useState('closed');

  const handlePress = () => {
    setAccordionState(accordionState === 'closed' ? 'opened' : 'closed');
  };

  const selectMonthCompete = useBreakpointValue({
    base: (
      <Pressable
        flexDir={'row'}
        onPress={handlePress}
        paddingLeft={Spacing['inline.xxxs']}
        paddingY={Spacing['inset.quarck']}
        borderRadius="2xl"
        alignItems="center"
        justifyContent="space-between">
        <Text fontSize={Typography['fontsize.md']} fontWeight={Typography['fontweight.semibold']}>
          {competence.label}
        </Text>
        <Icon
          size="xs"
          color={Color['neutral.dark']}
          name={accordionState === 'opened' ? 'ChevronUp' : 'ChevronDown'}
          testId="visible-icon"
        />
      </Pressable>
    ),
    sm: (
      <MenuSelect
        iconTitle
        triggerComponent={
          <Text
            marginRight={Spacing['inline.nano']}
            fontSize={Typography['fontsize.md']}
            fontWeight={Typography['fontweight.semibold']}>
            {competence.label}
          </Text>
        }
        items={Array.prototype.concat(
          [],
          ...months.map((month) => [
            {
              label: month.label,
              onPress: () => {
                setCompetence({ value: month.value, label: month.label });
                handlePress;
              },
            },
          ])
        )}
      />
    ),
  });

  return (
    <SliderModal dismissWhenClickOutside={false} width={width}>
      <VStack
        backgroundColor={Color['neutral.white']}
        p={Spacing['inline.xxs']}
        space={Spacing['stack.xs']}>
        <Heading
          testId="heading"
          display="flex"
          alignItems="center"
          fontSize={Typography['fontsize.lg']}
          variant="bold">
          <HStack space={Spacing['stack.quarck']} flex={1} alignItems="center">
            <Pressable onPress={goBack}>
              <Icon name="ArrowLeft" size="xs" testId="back-icon" />
            </Pressable>
            <Text variant="semibold">Importar nova planilha</Text>
            <Spacer />
            <Pressable onPress={goBack}>
              <Icon name="Close" size="xs" testId="close-icon" variant="rounded-border" />
            </Pressable>
          </HStack>
        </Heading>
        <Shadow
          p={Spacing['inline.xxxs']}
          testId="export-shadow"
          level={2}
          borderRadius={BoxRadius.sm}>
          <Pressable onPress={handleDownload}>
            <HStack justifyContent={'space-between'} alignItems={'center'}>
              <VStack space={Spacing['stack.quarck']}>
                <Text variant="semibold">Exportar planilha</Text>
                <Text
                  maxWidth={deviceType === 'mobile' ? 270 : 'auto'}
                  fontSize={Typography['fontsize.xs']}
                  color="neutral.medium">
                  Baixe a planilha base, insira os dados, em seguida, importe na plataforma.
                </Text>
              </VStack>

              <Icon
                name="Download"
                size="xs"
                testId="download-icon"
                color={Color['brand.tertiary.principal']}
              />
            </HStack>
          </Pressable>
        </Shadow>
        <Box>
          <Center>
            <VStack space={Spacing['stack.quarck']} alignItems="center">
              <Illustration name="SpreadsheetIllustration" testId="spreadsheet-illustration" />
              <Text textAlign={'center'} fontSize={Typography['fontsize.md']}>
                Precisamos que você anexe a{' '}
                <Text textAlign={'center'} fontWeight={Typography['fontweight.bold']}>
                  planilha base
                </Text>{' '}
                preenchida.
              </Text>
              <Text textAlign={'center'} fontSize={Typography['fontsize.xs']}>
                Lembre-se de preencher a planilha corretamente.
              </Text>
            </VStack>
          </Center>
        </Box>
        <HStack space={Spacing['stack.nano']} alignItems="center">
          {deviceType === 'desktop' && (
            <Icon
              name="Info"
              testId="info-icon"
              size="xs"
              color={Color['brand.tertiary.principal']}
            />
          )}
          <Text color="neutral.medium">
            {deviceType === 'mobile' ? 'Mês de competência' : 'Selecione o mês de competência'}
          </Text>

          {selectMonthCompete}
          <Spacer />
        </HStack>

        {uploadSection}
      </VStack>
      {accordionState === 'opened' && (
        <NewAccordion handlePress={handlePress}>
          <VStack paddingX={Spacing['inline.xxxs']} space={Spacing['stack.xxs']}>
            {months.map((month) => (
              <Pressable
                key={month.label}
                onPress={() => {
                  setCompetence({ value: month.value, label: month.label });
                  handlePress();
                }}>
                <Text
                  fontSize={Typography['fontsize.lg']}
                  fontWeight={Typography['fontweight.semibold']}>
                  {month.label}
                </Text>
              </Pressable>
            ))}
          </VStack>
        </NewAccordion>
      )}
    </SliderModal>
  );
};
